import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Pager } from "@/components/Pager";
import { FileText, Clock, CheckCircle2, Send, Plus, ArrowRight } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, tint, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
    <div className="flex items-center justify-between">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <p className="mt-4 font-head text-3xl font-extrabold text-slate-900 dark:text-white">{value}</p>
    <p className="mt-1 text-sm text-slate-500">{label}</p>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, en_cours: 0, termine: 0, envoyes: 0 });
  const [recent, setRecent] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });

  useEffect(() => {
    api.get("/interventions/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const loadPage = () => api.get("/interventions", { params: { page, limit: 20 } }).then((r) => {
      setRecent(r.data.items); setMeta({ total: r.data.total, pages: r.data.pages });
    }).catch(() => {});
    loadPage();
    window.addEventListener("ir-synced", loadPage);
    return () => window.removeEventListener("ir-synced", loadPage);
  }, [page]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Bonjour {user?.name?.split(" ")[0]}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Vue d'ensemble de vos bons d'intervention.</p>
        </div>
        <Button onClick={() => navigate("/interventions/nouveau")} data-testid="dashboard-new-btn"
          className="h-11 gap-2 bg-amber-500 font-semibold text-slate-900 hover:bg-amber-400">
          <Plus className="h-4 w-4" /> Nouveau bon
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-total" icon={FileText} label="Total interventions" value={stats.total}
          tint="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" />
        <StatCard testId="stat-encours" icon={Clock} label="En cours" value={stats.en_cours}
          tint="bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300" />
        <StatCard testId="stat-termine" icon={CheckCircle2} label="Terminés" value={stats.termine}
          tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" />
        <StatCard testId="stat-envoyes" icon={Send} label="PDF envoyés" value={stats.envoyes}
          tint="bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <h2 className="font-head text-lg font-bold text-slate-900 dark:text-white">Interventions récentes</h2>
          <Button variant="ghost" onClick={() => navigate("/interventions")} className="gap-1 text-sm text-blue-600" data-testid="see-all-btn">
            Tout voir <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        {recent.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-slate-400">Aucune intervention pour le moment.</div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {recent.map((it) => (
              <li key={it.id} data-testid={`recent-${it.id}`}
                onClick={() => navigate(`/interventions/${it.id}`)}
                className="flex cursor-pointer items-center gap-4 px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
                  <FileText className="h-5 w-5 text-slate-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{it.title}</p>
                  <p className="font-mono text-xs text-blue-600 dark:text-blue-400">{it.reference}</p>
                </div>
                <span className="hidden text-xs text-slate-400 sm:block">
                  {it.type === "pv_liberation" ? "PV libération" : "Journal chantier"}
                </span>
                <StatusBadge status={it.status} />
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-slate-100 px-5 py-3 dark:border-slate-800">
          <Pager page={page} pages={meta.pages} total={meta.total} onChange={setPage} testPrefix="dashboard-pager" />
        </div>
      </div>
    </div>
  );
}
